import {useResizableChartWidth} from '../../../../shared/components/d3chart/utils'
import {StockDevelopmentChartConfigProvider} from '../../../../shared/components/stockDevelopmentChart/StockDevelopmentChartConfigProvider'
import {STOCK_DEVELOPMENT_CHART_DEFAULT_HEIGHT} from '../../../../shared/components/stockDevelopmentChart/StockDevelopmentChartContent'
import {useStockDevelopmentChartConfigData} from '../../../../shared/components/stockDevelopmentChart/utils/useStockDevelopmentChartConfigData'
import {useMaterialStorageDataQuery, useStockDevelopmentQuery} from '../../../../shared/hooks/api'
import {useCurrentScheduleQuery} from '../../../../shared/hooks/api/schedules/useCurrentScheduleQuery'
import {usePlantConfig} from '../../../../shared/hooks/usePlantConfigData'
import {getListOfDays, MomentRange} from '../../../../shared/selectors/time'
import {usePlanningChartStartEnd} from '../../../page-planning/dailyPlanning/PlanningChartStartEndProvider'
import {useSelectedOptimizerPlan} from '../../SelectedOptimizerPlanProvider'
import {useSelectedMaterialId} from '../SelectedMaterialIdProvider'

import {OptimizerStockLevelMaterialDetailChartContent} from './OptimizerStockLevelMaterialDetailChartContent'

export const OptimizerStockLevelMaterialDetailChart = () => {
  const [chartRef, width] = useResizableChartWidth()

  const {materialId} = useSelectedMaterialId()
  if (materialId === undefined) {
    throw new Error('Must have selected material')
  }

  const {timezone_id: timezoneId} = usePlantConfig()
  const {startOfChart, endOfChart, startOfPlan, endOfPlan} = usePlanningChartStartEnd()
  const chartRange = [startOfChart, endOfChart] as MomentRange
  const planRange = [startOfPlan, endOfPlan] as MomentRange

  const {selectedInTableScheduleId} = useSelectedOptimizerPlan()

  const {data: stockDevelopment} = useStockDevelopmentQuery({
    materialIds: [materialId],
    scheduleId: selectedInTableScheduleId,
    timeframe: planRange
  })
  const {data: manualSchedule} = useCurrentScheduleQuery({range: chartRange})

  const isOptimizedPlanSelected = selectedInTableScheduleId !== undefined

  const {data: stockDevelopmentProductionPlan} = useStockDevelopmentQuery({
    materialIds: isOptimizedPlanSelected && manualSchedule ? [materialId] : [],
    scheduleId: manualSchedule?.scheduleId,
    timeframe: planRange
  })

  const {data: storageData} = useMaterialStorageDataQuery({materialId})

  const listOfDays = getListOfDays({start: chartRange[0], end: chartRange[1], additionalDays: 1})

  const configData = useStockDevelopmentChartConfigData({
    stockData: stockDevelopment
      ? stockDevelopment[materialId]
      : {
          stockLevel: {actual: [], forecast: [], combined: []},
          stockLevelHourly: {}
        },
    stockDataSecondary: stockDevelopmentProductionPlan?.[materialId],
    listOfDays: listOfDays,
    timezoneId,
    minTargetLevels: storageData
      ? {
          week: storageData.minTargetLevel,
          weekend: storageData.minTargetLevelWeekend
        }
      : undefined,
    storageCapacity: storageData?.storageCapacity,
    height: STOCK_DEVELOPMENT_CHART_DEFAULT_HEIGHT,
    width
  })

  return (
    <StockDevelopmentChartConfigProvider {...configData}>
      <OptimizerStockLevelMaterialDetailChartContent
        chartRef={chartRef}
        width={width}
        materialId={materialId}
      />
    </StockDevelopmentChartConfigProvider>
  )
}
